import React, { useState, useEffect } from 'react';
import './AgentStatusWidget.css';

interface AgentStatus {
  state: 'idle' | 'polling' | 'implementing' | 'creating-pr' | 'completed' | 'error';
  jiraTicket?: string;
  workflowExecutionId?: string;
  prUrl?: string;
  message?: string;
  timestamp: string;
}

interface AgentStatusWidgetProps {
  executionId?: string;
  jiraTicket?: string;
  workflowName?: string;
}

const AgentStatusWidget: React.FC<AgentStatusWidgetProps> = ({ executionId, jiraTicket, workflowName }) => {
  const [agentStatus, setAgentStatus] = useState<AgentStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showWidget, setShowWidget] = useState(false);

  // Only show widget for SonarQube workflows
  useEffect(() => {
    const isSonarQubeWorkflow = (workflowName?.toLowerCase().includes('sonar') ||
                                 workflowName?.toLowerCase().includes('security')) ?? false;
    setShowWidget(isSonarQubeWorkflow);
  }, [workflowName]);

  useEffect(() => {
    if (!showWidget) return;

    const fetchAgentStatus = async () => {
      try {
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.workflow.alokpandey.org';
        const accessToken = localStorage.getItem('accessToken');

        let url;
        // Try fetching by execution ID first
        if (executionId) {
          url = `${API_BASE_URL}/api/agent-status/execution/${executionId}`;
        }
        // Fall back to JIRA ticket
        else if (jiraTicket) {
          url = `${API_BASE_URL}/api/agent-status/jira/${jiraTicket}`;
        }

        if (url) {
          const response = await fetch(url, {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${accessToken}`
            }
          });

          const data = await response.json();
          if (data.success && data.data) {
            setAgentStatus(data.data);
          }
        }

        setIsLoading(false);
      } catch (error) {
        console.error('Failed to fetch agent status:', error);
        setIsLoading(false);
      }
    };

    // Initial fetch
    fetchAgentStatus();

    // Poll every 5 seconds
    const interval = setInterval(fetchAgentStatus, 5000);

    return () => clearInterval(interval);
  }, [executionId, jiraTicket, showWidget]);

  if (!showWidget) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="agent-status-widget loading">
        <div className="widget-header">
          <span className="widget-icon">🤖</span>
          <h3>Autonomous Agent Status</h3>
        </div>
        <div className="widget-body">
          <p className="loading-text">Loading agent status...</p>
        </div>
      </div>
    );
  }

  if (!agentStatus) {
    return (
      <div className="agent-status-widget idle">
        <div className="widget-header">
          <span className="widget-icon">🤖</span>
          <h3>Autonomous Agent</h3>
        </div>
        <div className="widget-body">
          <div className="status-message">
            <span className="status-icon">💤</span>
            <p>The autonomous remediation agent is monitoring for new security tickets.</p>
          </div>
          <div className="agent-info">
            <p className="info-text">
              When a JIRA ticket is created with labels <code>sonarqube</code> and <code>auto-generated</code>,
              the agent will automatically implement fixes and create a Pull Request.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const getStateIcon = (state: string) => {
    switch (state) {
      case 'polling': return '🔍';
      case 'implementing': return '⚙️';
      case 'creating-pr': return '📝';
      case 'completed': return '✅';
      case 'error': return '❌';
      default: return '💤';
    }
  };

  const getStateColor = (state: string) => {
    switch (state) {
      case 'polling': return 'blue';
      case 'implementing': return 'orange';
      case 'creating-pr': return 'purple';
      case 'completed': return 'green';
      case 'error': return 'red';
      default: return 'gray';
    }
  };

  const getStateLabel = (state: string) => {
    switch (state) {
      case 'polling': return 'Polling JIRA';
      case 'implementing': return 'Implementing Fixes';
      case 'creating-pr': return 'Creating Pull Request';
      case 'completed': return 'Completed';
      case 'error': return 'Error';
      default: return 'Idle';
    }
  };

  return (
    <div className={`agent-status-widget ${agentStatus.state}`}>
      <div className="widget-header">
        <span className="widget-icon">🤖</span>
        <h3>Autonomous Agent Status</h3>
        <span className={`status-badge ${getStateColor(agentStatus.state)}`}>
          {getStateIcon(agentStatus.state)} {getStateLabel(agentStatus.state)}
        </span>
      </div>
      
      <div className="widget-body">
        {agentStatus.jiraTicket && (
          <div className="status-row">
            <span className="label">JIRA Ticket:</span>
            <span className="value">
              <a 
                href={`https://maplelabs.atlassian.net/browse/${agentStatus.jiraTicket}`}
                target="_blank"
                rel="noopener noreferrer"
                className="jira-link"
              >
                {agentStatus.jiraTicket} ↗
              </a>
            </span>
          </div>
        )}

        {agentStatus.message && (
          <div className="status-row message-row">
            <span className="label">Status:</span>
            <span className="value">{agentStatus.message}</span>
          </div>
        )}

        {agentStatus.prUrl && (
          <div className="status-row pr-row">
            <span className="label">Pull Request:</span>
            <span className="value">
              <a 
                href={agentStatus.prUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="pr-link"
              >
                {agentStatus.prUrl} ↗
              </a>
            </span>
          </div>
        )}

        {agentStatus.timestamp && (
          <div className="status-row timestamp-row">
            <span className="label">Last Updated:</span>
            <span className="value timestamp">
              {new Date(agentStatus.timestamp).toLocaleString()}
            </span>
          </div>
        )}
      </div>

      {agentStatus.state === 'implementing' && (
        <div className="widget-footer">
          <div className="progress-indicator">
            <div className="spinner"></div>
            <span>Auggie CLI is analyzing and fixing security issues...</span>
          </div>
        </div>
      )}

      {agentStatus.state === 'completed' && agentStatus.prUrl && (
        <div className="widget-footer success">
          <p>✅ Security fixes have been implemented and a Pull Request has been created!</p>
        </div>
      )}

      {agentStatus.state === 'error' && (
        <div className="widget-footer error">
          <p>❌ An error occurred. Check the agent logs for details.</p>
        </div>
      )}
    </div>
  );
};

export default AgentStatusWidget;

